import { useEffect, useRef } from 'react';
import { UseFormClearErrors, UseFormRegister, UseFormSetValue } from 'react-hook-form';
import { P, Typography } from '@vp/swan';
import { __ } from '@99designs/i18n';
import { Format } from '@99designs/ninedown';
import { useFieldViewSkinContext } from '../../../context/FieldViewContext';
import { useBriefFormContext } from '../../BriefContext/BriefFormContext';
import { FormInput } from '../BriefForm';
import { useFieldProcessing } from '../FieldProcessingProvider';
import { ResizableTextArea, ResizableTextAreaRef } from '../ResizableTextArea';
import { Field_TextAreaField_Fragment } from '../brief.generated';
import { briefTestId } from '../testIds';
import { useDebouncedTextUpdate } from './text/useDebouncedTextUpdate';
import { useUpdateTextAreaField } from './text/useUpdateTextField';

export type TextAreaFieldProps = Field_TextAreaField_Fragment & {
    register: UseFormRegister<FormInput>;
    setValue: UseFormSetValue<FormInput>;
    clearErrors: UseFormClearErrors<FormInput>;
};

export function Input({
    id,
    register,
    setValue,
    required,
    placeholder,
    textAreaValue,
    subtext,
    maxLength,
    clearErrors,
    errorMessage,
}: TextAreaFieldProps) {
    const { briefId } = useBriefFormContext();
    const updateMutation = useUpdateTextAreaField(id, briefId, textAreaValue);
    const { setProcessingField } = useFieldProcessing();
    const debouncedTextUpdate = useDebouncedTextUpdate(id, updateMutation);
    const errorMsg = errorMessage || __('This field is required');

    const resizableTextAreaRef = useRef<ResizableTextAreaRef | null>(null);

    useEffect(() => {
        // any changes to this value from empty to non-empty will appear to fail validation
        // for the first mutation unless we explicitly set it.
        setValue(id, textAreaValue ?? '');

        // Avoid race condition where the textarea resize before the dom is fully rendered.
        setTimeout(() => {
            if (resizableTextAreaRef.current) {
                resizableTextAreaRef.current.resize();
            }
        }, 10);
    }, []);

    useEffect(() => {
        if (resizableTextAreaRef.current) {
            resizableTextAreaRef.current.resize();
        }
    }, [textAreaValue]);

    const { ref: reactHookFormRef, ...reactHookFormProps } = register(id, {
        required: required ? errorMsg : false,
        onChange: (e) => {
            setValue(id, e.target.value);
            if (resizableTextAreaRef.current) {
                resizableTextAreaRef.current.resize();
            }
            clearErrors(id);
            if (required && (e.target.value == null || e.target.value === '')) {
                return;
            }
            setProcessingField(id, true);
            debouncedTextUpdate(e.target.value);
        },
    });

    return (
        <>
            <ResizableTextArea
                data-testid={briefTestId('brief-field-textarea', id)}
                {...reactHookFormProps}
                {...(textAreaValue ? { defaultValue: textAreaValue } : {})}
                placeholder={placeholder || ''}
                maxLength={maxLength}
                fullWidth
                resize="vertical"
                rows={5}
                style={{ minHeight: '120px' }}
                aria-required={required ? true : undefined}
                ref={(el) => {
                    reactHookFormRef(el?.baseRef());
                    resizableTextAreaRef.current = el;
                }}
            />
            {subtext && (
                <P
                    fontSize="small"
                    textColor="subtle"
                    dangerouslySetInnerHTML={{
                        __html: Format(subtext),
                    }}
                    data-testid={briefTestId('brief-field-subtext', id)}
                />
            )}
        </>
    );
}

interface TextAreaFieldViewProps {
    textAreaValue: Field_TextAreaField_Fragment['textAreaValue'];
}

export function View({ textAreaValue }: TextAreaFieldViewProps) {
    const skin = useFieldViewSkinContext();

    if (!textAreaValue) return null;

    return (
        <Typography
            fontSize={skin.fontSize}
            style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
        >
            {textAreaValue}
        </Typography>
    );
}
